import {MutableRefObject, useCallback} from "react"
import {isMobile} from "react-device-detect"
import {useDropzone} from "react-dropzone"
import {v4 as uuid} from "uuid"
import cx from "classnames"
import {Icon} from "components/Icon"
import styles from "./item.module.css"
import {RemarkIndicator} from "./RemarkIndicator"
import {downloadLink, getFileExtension} from "helpers"
import {forEach, isEmpty, last} from "lodash"
import {Tooltip} from "antd"
import {CheckboxType, DigitalFieldUI} from "types/tasks"
import {FILE_UPLOADING_ACCEPT_FOR} from "types/files"

export const MOBILE_FONT_SIZE = {
    height: 14,
    extraSmallSize: 7,
    smallSize: 9
}

type Props = {
    field: DigitalFieldUI
    canSign: boolean
    errorFieldIds: string[]
    fieldRefs: MutableRefObject<Record<string, HTMLDivElement>>
    onChangeField: (fieldId: string, data: any) => void
}

export function File(props: Props) {
    const {field, canSign, errorFieldIds, fieldRefs, onChangeField} = props
    const {top, left, width, height} = field.placeInformation
    const {isYourField} = field

    let fontSize = Math.min(Math.floor(height / 1.7), MOBILE_FONT_SIZE.height)
    if (isMobile) {
        const minFontsize =
            height <= MOBILE_FONT_SIZE.height ? MOBILE_FONT_SIZE.extraSmallSize : MOBILE_FONT_SIZE.smallSize
        fontSize = Math.min(Math.floor(height / 1.7), minFontsize)
    }

    const onDrop = useCallback(
        (acceptedFiles) => {
            const newAcceptedFileInfos = acceptedFiles.map((file) => ({
                id: uuid(),
                extension: getFileExtension(file.name),
                file
            }))
            onChangeField(field.id, {isSigned: true, value: newAcceptedFileInfos})
        },
        [field.id, onChangeField]
    )

    const {getRootProps, getInputProps} = useDropzone({
        onDrop,
        multiple: field.fileType === CheckboxType.MULTIPLE,
        accept: FILE_UPLOADING_ACCEPT_FOR.DOCUMENT_AND_IMAGE
    })

    const downloadAllFiles = () => {
        forEach(field.value, (fileUrl) => downloadLink(fileUrl))
    }

    const getFileNames = () => {
        return field.value?.map((item) => (item.file ? item.file.name : last(item.split("/")))).join(", ")
    }

    const renderField = () => {
        const fileNames = getFileNames()

        return (
            <div
                {...getRootProps()}
                ref={(ref) => (fieldRefs.current[field.id] = ref)}
                className={cx(styles.actionItem, "cursor-pointer", {
                    [styles.actionItemRequired]: field.isYourField && field.required,
                    [styles.hasComment]: !!field.remarkComment,
                    [styles.actionItem_error]: errorFieldIds.includes(field.id)
                })}
                style={{width, height}}>
                <input {...getInputProps()} />
                <div className={styles.textWrap}>
                    {isEmpty(fileNames) ? (
                        <>
                            <Tooltip title={field.label}>
                                <p className={styles.label}>{field.label}</p>
                            </Tooltip>
                            <Icon icon="FILE_UP" className={styles.icon} />
                        </>
                    ) : (
                        <Tooltip title={fileNames}>
                            <div className={cx(styles.fileAnswer)} style={{fontSize}}>
                                {fileNames}
                            </div>
                        </Tooltip>
                    )}
                </div>
            </div>
        )
    }

    const renderBody = () => {
        if (canSign && isYourField) {
            return renderField()
        }

        return (
            <div
                className={cx(styles.actionItem, styles.flexCenter)}
                style={{
                    width,
                    height,
                    backgroundColor: "transparent",
                    borderColor: "transparent"
                }}>
                {field.value && (
                    <Tooltip title="Click this icon to download file">
                        <button className={styles.downloadBtn} onClick={downloadAllFiles}>
                            <Icon icon="FILE" className={styles.fileAnswerIcon} />
                        </button>
                    </Tooltip>
                )}
            </div>
        )
    }

    return (
        <>
            <div className={styles.wrap} style={{left, top}}>
                {renderBody()}
            </div>
            {field.remarkComment && (
                <div className={styles.wrap} style={{left, top, zIndex: 9}}>
                    <RemarkIndicator field={field} />
                </div>
            )}
        </>
    )
}
